import React, { useState, useEffect } from 'react'
import { api } from '../services/api'
import { 
  Users, 
  FileText, 
  Calendar, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Play
} from 'lucide-react'

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadMetrics()
  }, [])

  const loadMetrics = async () => {
    try {
      setLoading(true)
      const response = await api.get('/dashboard/metrics.php')
      
      if (response.ok) {
        setMetrics(response)
        setError(null)
      } else {
        // Fallback: buscar apenas total de pessoas
        const peopleResponse = await api.get('/pessoas/list.php', { per_page: 1 })
        if (peopleResponse.ok) {
          setMetrics({
            total_pessoas: peopleResponse.total || 0,
            total_atendimentos: 0,
            aniversariantes: 0,
            ultimos: [],
            status: { aguardando: 0, andamento: 0, encerrado: 0, concluido: 0 }
          })
        } else {
          setError('Não foi possível carregar as métricas')
        }
      }
    } catch (err) {
      console.error('Erro ao carregar métricas:', err)
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'aguardando':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20'
      case 'andamento':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20'
      case 'encerrado':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20'
      case 'concluido':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aguardando':
        return <Clock className="h-5 w-5" />
      case 'andamento':
        return <Play className="h-5 w-5" />
      case 'encerrado':
        return <AlertCircle className="h-5 w-5" />
      case 'concluido':
        return <CheckCircle className="h-5 w-5" />
      default:
        return <Clock className="h-5 w-5" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          Erro ao carregar dashboard
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {error}
        </p>
        <div className="mt-6">
          <button
            onClick={loadMetrics}
            className="btn-primary"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-transition">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Visão geral do sistema Gabinett
        </p>
      </div>

      {/* Cards de métricas */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Total de Pessoas
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {metrics?.total_pessoas?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Total de Atendimentos
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {metrics?.total_atendimentos?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Aniversariantes
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {metrics?.aniversariantes || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Últimos Cadastros
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {metrics?.ultimos?.length || '0'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Status dos atendimentos */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Status dos Atendimentos
          </h3>
          <div className="space-y-3">
            {metrics?.status && Object.entries(metrics.status).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                    {getStatusIcon(status)}
                    <span className="ml-1 capitalize">{status}</span>
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Últimos Cadastrados
          </h3>
          <div className="space-y-2">
            {metrics?.ultimos && metrics.ultimos.length > 0 ? (
              metrics.ultimos.map((nome, index) => (
                <div key={index} className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      {nome.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="ml-3 text-sm text-gray-900 dark:text-white">
                    {nome}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhum cadastro recente
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Gráfico de donut simples com CSS */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Distribuição por Status
        </h3>
        <div className="flex justify-center">
          <div className="relative w-48 h-48">
            {metrics?.status && (
              <svg className="w-48 h-48 transform -rotate-90" viewBox="0 0 100 100">
                {(() => {
                  const total = Object.values(metrics.status).reduce((sum, count) => sum + count, 0)
                  if (total === 0) return null
                  
                  let currentAngle = 0
                  const colors = ['#fbbf24', '#3b82f6', '#6b7280', '#10b981']
                  const statuses = ['aguardando', 'andamento', 'encerrado', 'concluido']
                  
                  return statuses.map((status, index) => {
                    const count = metrics.status[status] || 0
                    const percentage = total > 0 ? (count / total) * 100 : 0
                    const angle = (percentage / 100) * 360
                    
                    if (percentage === 0) return null
                    
                    const x1 = 50 + 40 * Math.cos((currentAngle * Math.PI) / 180)
                    const y1 = 50 + 40 * Math.sin((currentAngle * Math.PI) / 180)
                    const x2 = 50 + 40 * Math.cos(((currentAngle + angle) * Math.PI) / 180)
                    const y2 = 50 + 40 * Math.sin(((currentAngle + angle) * Math.PI) / 180)
                    
                    const largeArcFlag = angle > 180 ? 1 : 0
                    
                    const pathData = [
                      `M 50 50`,
                      `L ${x1} ${y1}`,
                      `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      'Z'
                    ].join(' ')
                    
                    currentAngle += angle
                    
                    return (
                      <path
                        key={status}
                        d={pathData}
                        fill={colors[index]}
                        className="transition-all duration-300 hover:opacity-80"
                      />
                    )
                  })
                })()}
              </svg>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {metrics?.total_atendimentos || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
